import { useCallback, useEffect, useState } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import tenantBg from "../assets/lbg.mp4";

const categories = [
  "Plumbing",
  "Electrical",
  "Security",
  "Cleaning",
  "Noise",
  "Structural",
  "Other",
];



export default function SubmitReport() {
  const [tenantName, setTenantName] = useState(
    localStorage.getItem("username") || ""
  );
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("Plumbing");
  const [propertyId, setPropertyId] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const fetchRentals = useCallback(async () => {
    try {
      const res = await api.get("/my-rentals");
      const linked = Array.isArray(res.data) ? [...res.data].sort((a, b) => Number(a.id) - Number(b.id)) : [];
      setRentals(linked);
      setPropertyId(String(linked[0]?.id || ""));
      const profile = await api.get("/profile");
      setTenantName(profile.data?.name || "");
    } catch (err) {
      console.error(err);
      setRentals([]);
    }
  }, []);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  useEffect(() => {
    if (!attachment || !attachment.type.startsWith("image/")) {
      setAttachmentPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(attachment);
    setAttachmentPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [attachment]);

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timeout);
  }, [toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantName || !propertyId || !subject || !message) {
      setToast({ type: "error", message: "Please fill in all required fields." });
      return;
    }

    setSubmitting(true);

    try {
      const form = new FormData();
      form.append("tenant_name", tenantName);
      form.append("subject", subject);
      form.append("message", message);
      form.append("category", category);
      if (propertyId) form.append("property_id", propertyId);
      if (attachment) form.append("attachment", attachment);

      await api.post("/reports", form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}` || "",
        },
      });

      setToast({ type: "success", message: "Report submitted successfully." });
      setSubject("");
      setMessage("");
      setCategory("Plumbing");
      setAttachment(null);
      setTimeout(() => navigate("/my-reports"), 700);
    } catch (err) {
      console.error(err.response || err);
      setToast({ type: "error", message: "Failed to submit report." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30";

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <video data-workspace-background="true" aria-hidden="true"
        src={tenantBg}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div className="absolute inset-0 bg-slate-950/86"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80"></div>

      {toast && (
        <div
          className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
            toast.type === "success" ? "bg-teal-600" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-16 text-white sm:px-6 md:py-10 lg:px-8">
        <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(460px,1fr)] lg:items-center">
          <section className="hidden lg:block">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Maintenance Request
            </p>
            <h1 className="mt-4 text-4xl font-bold tracking-wide text-white">
              Submit a Report
            </h1>
            <p className="mt-3 max-w-xl text-slate-300">
              Categorize the issue, attach proof, and track admin
              updates from your report history.
            </p>

            <div className="mt-8 grid gap-3">
              <div className="rounded-lg border border-white/10 bg-slate-950/80 p-4 shadow-xl">
                <p className="text-sm font-semibold text-white">
                  Better information, faster action
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  Category, details, and photos help admins route the report
                  correctly.
                </p>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-950/80 p-4 shadow-xl">
                <p className="text-sm font-semibold text-white">
                  Trackable workflow
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  Submitted reports now show progress and resolution timing.
                </p>
              </div>
            </div>
          </section>

          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="w-full rounded-lg border border-white/10 bg-slate-950/90 p-6 shadow-2xl sm:p-8"
          >
            <div className="mb-6 flex items-start justify-between gap-4 border-b border-white/10 pb-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-teal-300 lg:hidden">
                  Maintenance Request
                </p>
                <h2 className="mt-1 text-2xl font-bold text-white">
                  Submit a Report
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Report an issue or concern regarding your rental.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/tenant/home")}
                className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
              >
                Back
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="submitreport-field-1" className="mb-2 block text-sm font-semibold text-slate-200">
                  Tenant Name
                </label>
                <input id="submitreport-field-1"
                  value={tenantName}
                  readOnly
                  className={inputClass}
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="submitreport-field-2" className="mb-2 block text-sm font-semibold text-slate-200">
                  Category
                </label>
                <select id="submitreport-field-2"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={inputClass}
                >
                  {categories.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="submitreport-field-4" className="mb-2 block text-sm font-semibold text-slate-200">
                  Related Rental
                </label>
                <select id="submitreport-field-4"
                  value={propertyId}
                  disabled
                  className={inputClass}
                >
                  <option value="">No linked rental available</option>
                  {rentals.map((rental) => (
                    <option key={rental.id} value={rental.id}>
                      {rental.property_name} - {rental.location}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="submitreport-field-5" className="mb-2 block text-sm font-semibold text-slate-200">
                Subject
              </label>
              <input id="submitreport-field-5"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={inputClass}
                placeholder="e.g. Broken door lock"
              />
            </div>

            <div className="mt-4">
              <label htmlFor="submitreport-field-6" className="mb-2 block text-sm font-semibold text-slate-200">
                Message
              </label>
              <textarea id="submitreport-field-6"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className={`${inputClass} resize-none`}
                placeholder="Describe the issue in detail"
              />
            </div>

            <div className="mt-4 rounded-lg border border-dashed border-white/15 bg-white/5 p-4">
              <label htmlFor="submitreport-field-7" className="mb-2 block text-sm font-semibold text-slate-200">
                Attachment
              </label>
              <input id="submitreport-field-7"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                key={attachment ? attachment.name : "report-attachment"}
                className="w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-teal-600 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-teal-500"
              />
              {attachment && (
                <div className="mt-3 rounded-lg border border-white/10 bg-slate-950/80 p-3">
                  <p className="text-sm font-semibold text-white">
                    {attachment.name}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    {(attachment.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  {attachmentPreview && (
                    <img
                      src={attachmentPreview}
                      alt="Attachment preview"
                      className="mt-3 max-h-52 w-full rounded object-contain"
                    />
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={submitting || !tenantName || !propertyId}
                className="flex-1 rounded-lg bg-teal-600 py-3 text-lg font-semibold text-white transition hover:bg-teal-500 disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Report"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/my-reports")}
                className="flex-1 rounded-lg border border-white/15 py-3 text-lg font-semibold text-slate-100 transition hover:border-teal-300/60 hover:bg-teal-500/15 hover:text-teal-100"
              >
                View Reports
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
